import { useState } from 'react';
import { CheckCircle, XCircle, MessageSquare } from 'lucide-react';
import { ProjectData } from '../types';
import { TestingAccessInstructions } from './TestingAccessInstructions';

interface CustomerApprovalProps {
  project: ProjectData;
  onApprove: (approved: boolean, comments?: string) => Promise<void>;
  isSubmitting?: boolean;
}

export function CustomerApproval({ project, onApprove, isSubmitting = false }: CustomerApprovalProps) {
  const [comments, setComments] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleApproval = async (approved: boolean) => {
    setIsLoading(true);
    try {
      await onApprove(approved, comments.trim() || undefined);
    } finally {
      setIsLoading(false);
    }
  };

  if (project.status !== 'customer_review') {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-cyan-100 rounded-lg">
          <CheckCircle className="h-6 w-6 text-cyan-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">App Store Submission Approval</h3>
          <p className="text-sm text-gray-600">
            Your application is ready for App Store submission. Please review and approve.
          </p>
        </div>
      </div>

      <TestingAccessInstructions
        googlePlayInternalTestLink={project.formData?.googlePlayInternalTestLink}
        jiraKey={project.jiraKey}
      />

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• We'll submit your app to the App Store for review</li>
          <li>• Apple will review your app (typically 24-48 hours)</li>
          <li>• You'll be notified once your app is approved and live</li>
        </ul>
      </div>

      <div className="mb-6">
        <label htmlFor="approval-comments" className="block text-sm font-medium text-gray-700 mb-2">
          <MessageSquare className="h-4 w-4 inline mr-1" />
          Comments (Optional)
        </label>
        <textarea
          id="approval-comments"
          value={comments}
          onChange={(e) => setComments(e.target.value)}
          placeholder="Add any comments or feedback about your app..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          rows={3}
        />
      </div>

      <div className="flex gap-3">
        <button
          onClick={() => handleApproval(true)}
          disabled={isLoading || isSubmitting}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CheckCircle className="h-4 w-4" />
          {isLoading ? 'Approving...' : 'Approve for App Store'}
        </button>
        
        <button
          onClick={() => handleApproval(false)}
          disabled={isLoading || isSubmitting}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <XCircle className="h-4 w-4" />
          {isLoading ? 'Declining...' : 'Request Changes'}
        </button>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        <p>
          <strong>Note:</strong> Once approved, your app will be submitted to the App Store. 
          If you need changes, please decline and provide feedback in the comments above.
        </p>
      </div>
    </div>
  );
}
